<script lang="ts">
  import { authState } from '../../lib/auth.svelte';
  import { db } from '../../lib/firebase';
  import { doc, getDoc, collection, addDoc, query, where, getDocs, updateDoc, orderBy } from 'firebase/firestore';
  import { onMount } from 'svelte';
  import { queueBusinessInvitationEmail, type BusinessInvitationEmailData } from '../../lib/api';
  import type { BusinessInvitation, Warehouse } from '../../lib/types';

  // Access guard - AccountAdmin and SystemAdmin can access business invitations
  let hasAccess = $derived(!authState.loading && (authState.isAccountAdmin || authState.isSystemAdmin));

  // State
  let businessInvitations = $state<BusinessInvitation[]>([]);
  let outsourcedWarehouses = $state<Warehouse[]>([]);
  let loading = $state(true);
  let saveError = $state('');

  let showAddModal = $state(false);
  let showInviteLinkModal = $state(false);
  let generatedInviteLink = $state('');
  let copiedToClipboard = $state(false);
  let emailSendStatus = $state<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  let emailSendError = $state('');

  // Form state
  let inviteeEmail = $state('');
  let inviteeName = $state('');
  let inviteeCompanyName = $state('');
  let selectedWarehouseIds = $state<string[]>([]);
  let formError = $state('');
  let formLoading = $state(false);

  // User document reference
  function getUserDocRef() {
    if (!authState.user) return null;
    return doc(db, 'users', authState.user.uid);
  }

  // Get business info for invitation
  async function getBusinessInfo(): Promise<{ id: string | null; name: string }> {
    const userDocRef = getUserDocRef();
    if (!userDocRef) return { id: null, name: 'EQUOS Workspace' };

    try {
      const docSnap = await getDoc(userDocRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.businesses && data.businesses.length > 0) {
          const selectedId = data.selectedBusinessId;
          const business = selectedId
            ? data.businesses.find((b: any) => b.id === selectedId)
            : data.businesses[0];
          return {
            id: business?.id || null,
            name: business?.name || 'EQUOS Workspace',
          };
        }
      }
    } catch (error) {
      console.error('Error getting business info:', error);
    }
    return { id: null, name: 'EQUOS Workspace' };
  }

  // Load outsourced warehouses owned by current user
  async function loadOutsourcedWarehouses() {
    if (!authState.user) return;

    try {
      // Query warehousedata for outsourced warehouses owned by current user
      const warehouseRef = collection(db, 'warehousedata');
      const q = query(
        warehouseRef,
        where('isOutsourced', '==', true),
        where('ownerId', '==', authState.user.uid)
      );
      const snapshot = await getDocs(q);

      outsourcedWarehouses = snapshot.docs
        .map(doc => ({
          id: doc.id,
          name: doc.data().warehouseName || doc.data().name || 'Unnamed Warehouse',
          address: doc.data().address,
          isActive: doc.data().isActive !== false,
          isOutsourced: doc.data().isOutsourced,
          ownerId: doc.data().ownerId,
        }))
        .filter(w => w.isActive);
    } catch (error) {
      console.error('Error loading outsourced warehouses:', error);
    }
  }

  // Load business invitations created by current user
  async function loadBusinessInvitations() {
    if (!authState.user) return;

    try {
      const invitationsRef = collection(db, 'business_invitations');
      const q = query(
        invitationsRef,
        where('invitedBy', '==', authState.user.uid),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);

      businessInvitations = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as BusinessInvitation[];
    } catch (error) {
      console.error('Error loading business invitations:', error);
    }
  }

  // Load data on mount
  onMount(async () => {
    if (authState.user) {
      await Promise.all([
        loadOutsourcedWarehouses(),
        loadBusinessInvitations(),
      ]);
    }
    loading = false;
  });

  // Generate a random invite code
  function generateInviteCode(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let code = '';
    for (let i = 0; i < 16; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  }

  function openAddModal() {
    inviteeEmail = '';
    inviteeName = '';
    inviteeCompanyName = '';
    selectedWarehouseIds = outsourcedWarehouses.map(w => w.id); // Default all selected
    formError = '';
    showAddModal = true;
  }

  function closeAddModal() {
    showAddModal = false;
    formError = '';
  }

  // Toggle warehouse selection
  function toggleWarehouse(warehouseId: string) {
    if (selectedWarehouseIds.includes(warehouseId)) {
      selectedWarehouseIds = selectedWarehouseIds.filter(id => id !== warehouseId);
    } else {
      selectedWarehouseIds = [...selectedWarehouseIds, warehouseId];
    }
  }

  async function handleCreateInvitation(e: Event) {
    e.preventDefault();
    formError = '';

    if (!inviteeEmail.trim() || !inviteeName.trim() || !inviteeCompanyName.trim()) {
      formError = 'Please fill in all fields';
      return;
    }

    if (selectedWarehouseIds.length === 0) {
      formError = 'Please select at least one warehouse to link';
      return;
    }

    // Check if already has pending invitation
    if (businessInvitations.some(i =>
      i.inviteeEmail.toLowerCase() === inviteeEmail.toLowerCase() &&
      i.status === 'pending'
    )) {
      formError = 'A pending invitation already exists for this email';
      return;
    }

    formLoading = true;

    try {
      const businessInfo = await getBusinessInfo();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 30); // 30 days expiry

      // Get selected warehouse names
      const warehouseNames = selectedWarehouseIds
        .map(id => outsourcedWarehouses.find(w => w.id === id)?.name)
        .filter(Boolean) as string[];

      const invitationData = {
        type: 'business_invitation' as const,
        status: 'pending' as const,
        inviteeEmail: inviteeEmail.trim().toLowerCase(),
        inviteeName: inviteeName.trim(),
        inviteeCompanyName: inviteeCompanyName.trim(),
        invitedBy: authState.user!.uid,
        inviterEmail: authState.user!.email,
        inviterBusinessId: businessInfo.id,
        inviterBusinessName: businessInfo.name,
        linkedWarehouseIds: selectedWarehouseIds,
        warehouseNames: warehouseNames,
        createdAt: new Date().toISOString(),
        expiresAt: expiresAt.toISOString(),
        acceptedAt: null,
        createdBusinessId: null,
        createdUserId: null,
      };

      const invitationsRef = collection(db, 'business_invitations');
      const docRef = await addDoc(invitationsRef, invitationData);

      // Add to local state
      businessInvitations = [{ id: docRef.id, ...invitationData }, ...businessInvitations];

      // Generate invite link
      const baseUrl = window.location.origin;
      const consolePath = import.meta.env.BASE_URL || '/console/';
      generatedInviteLink = `${baseUrl}${consolePath}#signup?business_invite=${docRef.id}`;

      closeAddModal();
      showInviteLinkModal = true;
      copiedToClipboard = false;

      // Queue invitation email
      emailSendStatus = 'sending';
      emailSendError = '';

      const emailData: BusinessInvitationEmailData = {
        email: invitationData.inviteeEmail,
        inviteeName: invitationData.inviteeName,
        inviteeCompanyName: invitationData.inviteeCompanyName,
        inviterName: authState.user?.displayName || authState.user?.email || 'Account Admin',
        inviterEmail: authState.user?.email || '',
        inviterBusinessName: businessInfo.name,
        warehouseNames: warehouseNames,
        inviteLink: generatedInviteLink,
        expiresAt: expiresAt.toLocaleDateString('en-AU', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
      };

      const emailResult = await queueBusinessInvitationEmail(emailData);

      if (emailResult.success) {
        emailSendStatus = 'sent';
      } else {
        emailSendStatus = 'failed';
        emailSendError = emailResult.error || 'Failed to queue email';
        console.error('Failed to queue business invitation email:', emailResult.error);
      }
    } catch (error: any) {
      console.error('Error creating business invitation:', error);
      formError = 'Failed to create invitation. Please try again.';
    } finally {
      formLoading = false;
    }
  }

  // Copy invite link to clipboard
  async function copyInviteLink() {
    try {
      await navigator.clipboard.writeText(generatedInviteLink);
      copiedToClipboard = true;
      setTimeout(() => {
        copiedToClipboard = false;
      }, 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  }

  // Close invite link modal
  function closeInviteLinkModal() {
    showInviteLinkModal = false;
    generatedInviteLink = '';
    emailSendStatus = 'idle';
    emailSendError = '';
  }

  // Revoke an invitation
  async function revokeInvitation(invitation: BusinessInvitation) {
    if (!confirm(`Revoke invitation for ${invitation.inviteeCompanyName}?`)) return;

    try {
      const inviteDocRef = doc(db, 'business_invitations', invitation.id);
      await updateDoc(inviteDocRef, { status: 'revoked' });

      businessInvitations = businessInvitations.map(i =>
        i.id === invitation.id ? { ...i, status: 'revoked' as const } : i
      );
    } catch (error) {
      console.error('Error revoking invitation:', error);
      alert('Failed to revoke invitation. Please try again.');
    }
  }

  // Resend invitation
  async function resendInvitation(invitation: BusinessInvitation) {
    emailSendStatus = 'sending';
    emailSendError = '';

    try {
      const baseUrl = window.location.origin;
      const consolePath = import.meta.env.BASE_URL || '/console/';
      const inviteLink = `${baseUrl}${consolePath}#signup?business_invite=${invitation.id}`;

      const emailData: BusinessInvitationEmailData = {
        email: invitation.inviteeEmail,
        inviteeName: invitation.inviteeName,
        inviteeCompanyName: invitation.inviteeCompanyName,
        inviterName: authState.user?.displayName || authState.user?.email || 'Account Admin',
        inviterEmail: authState.user?.email || '',
        inviterBusinessName: invitation.inviterBusinessName,
        warehouseNames: invitation.warehouseNames,
        inviteLink: inviteLink,
        expiresAt: new Date(invitation.expiresAt).toLocaleDateString('en-AU', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
      };

      const emailResult = await queueBusinessInvitationEmail(emailData);

      if (emailResult.success) {
        emailSendStatus = 'sent';
        generatedInviteLink = inviteLink;
        showInviteLinkModal = true;
        copiedToClipboard = false;
      } else {
        emailSendStatus = 'failed';
        emailSendError = emailResult.error || 'Failed to queue email';
        alert(`Failed to queue email: ${emailSendError}\n\nYou can still copy the invite link manually.`);
        generatedInviteLink = inviteLink;
        showInviteLinkModal = true;
        copiedToClipboard = false;
      }
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      emailSendStatus = 'failed';
      emailSendError = error.message || 'Failed to queue email';
      alert(`Error resending invitation: ${error.message}`);
    }
  }

  function getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'accepted': return 'status-accepted';
      case 'expired': return 'status-expired';
      case 'revoked': return 'status-revoked';
      default: return 'status-pending';
    }
  }

  // Check if invitation is expired
  function isExpired(invitation: BusinessInvitation): boolean {
    return new Date() > new Date(invitation.expiresAt);
  }

  // Derived lists
  let pendingInvitations = $derived(
    businessInvitations.filter(i => i.status === 'pending' && !isExpired(i))
  );
  let acceptedInvitations = $derived(
    businessInvitations.filter(i => i.status === 'accepted')
  );
  let expiredOrRevokedInvitations = $derived(
    businessInvitations.filter(i =>
      i.status === 'revoked' || i.status === 'expired' || (i.status === 'pending' && isExpired(i))
    )
  );
</script>

{#if authState.loading}
  <section class="invitations-section">
    <div class="loading-state">
      <div class="spinner"></div>
      <p>Loading...</p>
    </div>
  </section>
{:else if !hasAccess}
  <section class="invitations-section">
    <div class="access-denied">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
      <h3>Access Restricted</h3>
      <p>Business invitations are only available to account owners with outsourced warehouses.</p>
    </div>
  </section>
{:else if outsourcedWarehouses.length === 0 && !loading}
  <section class="invitations-section">
    <div class="empty-state">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9,22 9,12 15,12 15,22" />
      </svg>
      <h3>No Outsourced Warehouses</h3>
      <p>You need to have at least one outsourced (3PL) warehouse to invite businesses.</p>
      <p class="hint">Create an outsourced warehouse in the Businesses section first.</p>
    </div>
  </section>
{:else}
<section class="invitations-section">
  <div class="section-header">
    <div>
      <h2>Business Invitations</h2>
      <p class="section-description">Invite businesses to use your 3PL warehouse services</p>
    </div>
    {#if !loading}
      <button class="btn-primary" onclick={openAddModal}>
        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" />
        </svg>
        Invite Business
      </button>
    {/if}
  </div>

  {#if saveError}
    <div class="error-banner">{saveError}</div>
  {/if}

  {#if loading}
    <div class="loading-state">
      <div class="spinner"></div>
      <p>Loading invitations...</p>
    </div>
  {:else}
    <!-- Your Warehouses -->
    <div class="warehouses-info">
      <h4>Your 3PL Warehouses ({outsourcedWarehouses.length})</h4>
      <div class="warehouse-tags">
        {#each outsourcedWarehouses as warehouse}
          <span class="warehouse-tag">{warehouse.name}</span>
        {/each}
      </div>
    </div>

    <!-- Pending Invitations -->
    {#if pendingInvitations.length > 0}
      <div class="invitations-list">
        <h4>Pending Invitations ({pendingInvitations.length})</h4>
        {#each pendingInvitations as invitation}
          <div class="invitation-card">
            <div class="invitation-header">
              <div class="company-info">
                <div class="company-avatar">
                  {invitation.inviteeCompanyName.charAt(0).toUpperCase()}
                </div>
                <div class="company-details">
                  <span class="company-name">{invitation.inviteeCompanyName}</span>
                  <span class="contact-info">{invitation.inviteeName} &bull; {invitation.inviteeEmail}</span>
                </div>
              </div>
              <span class="status-badge {getStatusBadgeClass(invitation.status)}">
                {invitation.status}
              </span>
            </div>
            <div class="invitation-meta">
              <span class="warehouses-label">
                Warehouses: {invitation.warehouseNames.join(', ')}
              </span>
              <span class="expires-label">
                Expires: {new Date(invitation.expiresAt).toLocaleDateString()}
              </span>
            </div>
            <div class="invitation-actions">
              <button class="btn-secondary-sm" onclick={() => resendInvitation(invitation)}>
                Resend
              </button>
              <button class="btn-danger-sm" onclick={() => revokeInvitation(invitation)}>
                Revoke
              </button>
            </div>
          </div>
        {/each}
      </div>
    {/if}

    <!-- Accepted Invitations -->
    {#if acceptedInvitations.length > 0}
      <div class="invitations-list">
        <h4>Accepted Businesses ({acceptedInvitations.length})</h4>
        {#each acceptedInvitations as invitation}
          <div class="invitation-card accepted">
            <div class="invitation-header">
              <div class="company-info">
                <div class="company-avatar accepted">
                  {invitation.inviteeCompanyName.charAt(0).toUpperCase()}
                </div>
                <div class="company-details">
                  <span class="company-name">{invitation.inviteeCompanyName}</span>
                  <span class="contact-info">{invitation.inviteeName} &bull; {invitation.inviteeEmail}</span>
                </div>
              </div>
              <span class="status-badge {getStatusBadgeClass(invitation.status)}">
                {invitation.status}
              </span>
            </div>
            <div class="invitation-meta">
              <span class="warehouses-label">
                Warehouses: {invitation.warehouseNames.join(', ')}
              </span>
              <span class="accepted-label">
                Joined: {invitation.acceptedAt ? new Date(invitation.acceptedAt).toLocaleDateString() : 'N/A'}
              </span>
            </div>
          </div>
        {/each}
      </div>
    {/if}

    <!-- Expired/Revoked -->
    {#if expiredOrRevokedInvitations.length > 0}
      <details class="expired-section">
        <summary>Expired/Revoked ({expiredOrRevokedInvitations.length})</summary>
        <div class="invitations-list">
          {#each expiredOrRevokedInvitations as invitation}
            <div class="invitation-card inactive">
              <div class="invitation-header">
                <div class="company-info">
                  <div class="company-avatar inactive">
                    {invitation.inviteeCompanyName.charAt(0).toUpperCase()}
                  </div>
                  <div class="company-details">
                    <span class="company-name">{invitation.inviteeCompanyName}</span>
                    <span class="contact-info">{invitation.inviteeEmail}</span>
                  </div>
                </div>
                <span class="status-badge {getStatusBadgeClass(isExpired(invitation) && invitation.status === 'pending' ? 'expired' : invitation.status)}">
                  {isExpired(invitation) && invitation.status === 'pending' ? 'expired' : invitation.status}
                </span>
              </div>
            </div>
          {/each}
        </div>
      </details>
    {/if}

    {#if businessInvitations.length === 0}
      <div class="empty-invitations">
        <p>No business invitations yet. Click "Invite Business" to get started.</p>
      </div>
    {/if}
  {/if}
</section>
{/if}

<!-- Add Business Invitation Modal -->
{#if showAddModal}
  <div class="modal-overlay" onclick={closeAddModal}>
    <div class="modal" onclick={(e) => e.stopPropagation()}>
      <div class="modal-header">
        <h3>Invite Business</h3>
        <button class="btn-close" onclick={closeAddModal}>&times;</button>
      </div>

      <form onsubmit={handleCreateInvitation}>
        {#if formError}
          <div class="form-error">{formError}</div>
        {/if}

        <div class="form-group">
          <label for="company-name">Business Name</label>
          <input
            id="company-name"
            type="text"
            bind:value={inviteeCompanyName}
            placeholder="Acme Corp"
            disabled={formLoading}
          />
          <small class="form-hint">The name of the business you're inviting</small>
        </div>

        <div class="form-group">
          <label for="contact-name">Contact Name</label>
          <input
            id="contact-name"
            type="text"
            bind:value={inviteeName}
            placeholder="John Smith"
            disabled={formLoading}
          />
        </div>

        <div class="form-group">
          <label for="contact-email">Contact Email</label>
          <input
            id="contact-email"
            type="email"
            bind:value={inviteeEmail}
            placeholder="john@acme.com"
            disabled={formLoading}
          />
          <small class="form-hint">They'll receive an invitation email at this address</small>
        </div>

        <div class="form-group">
          <label>Link to Warehouses</label>
          <div class="checkbox-group">
            {#each outsourcedWarehouses as warehouse}
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedWarehouseIds.includes(warehouse.id)}
                  onchange={() => toggleWarehouse(warehouse.id)}
                  disabled={formLoading}
                />
                {warehouse.name}
              </label>
            {/each}
          </div>
          <small class="form-hint">Select which warehouses this business can use</small>
        </div>

        <div class="info-box">
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
          </svg>
          <div>
            <strong>What happens next?</strong>
            <p>The invitee will receive an email with a link to create their own EQUOS account. Once they sign up, they'll have their own business workspace with access to your selected warehouses.</p>
          </div>
        </div>

        <div class="modal-actions">
          <button type="button" class="btn-secondary" onclick={closeAddModal} disabled={formLoading}>
            Cancel
          </button>
          <button type="submit" class="btn-primary" disabled={formLoading}>
            {formLoading ? 'Sending...' : 'Send Invitation'}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}

<!-- Invite Link Modal -->
{#if showInviteLinkModal}
  <div class="modal-overlay" onclick={closeInviteLinkModal}>
    <div class="modal" onclick={(e) => e.stopPropagation()}>
      <div class="modal-header">
        <h3>Invitation Created</h3>
        <button class="btn-close" onclick={closeInviteLinkModal}>&times;</button>
      </div>

      <div class="modal-body">
        <!-- Email Status -->
        {#if emailSendStatus === 'sending'}
          <div class="email-status sending">
            <div class="spinner-small"></div>
            <span>Queuing invitation email...</span>
          </div>
        {:else if emailSendStatus === 'sent'}
          <div class="email-status success">
            <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
            <span>Invitation email queued for delivery!</span>
          </div>
        {:else if emailSendStatus === 'failed'}
          <div class="email-status error">
            <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            <span>Email failed: {emailSendError}</span>
          </div>
        {/if}

        <p style="margin-top: 1rem;">Share this link with the business to complete their registration:</p>

        <div class="invite-link-container">
          <input type="text" readonly value={generatedInviteLink} class="invite-link-input" />
          <button class="btn-copy" onclick={copyInviteLink}>
            {#if copiedToClipboard}
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
              Copied!
            {:else}
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path d="M8 2a1 1 0 000 2h2a1 1 0 100-2H8z" />
                <path d="M3 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v6h-4.586l1.293-1.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L10.414 13H15v3a2 2 0 01-2 2H5a2 2 0 01-2-2V5zM15 11h2a1 1 0 110 2h-2v-2z" />
              </svg>
              Copy
            {/if}
          </button>
        </div>

        <p class="form-help" style="margin-top: 1rem;">
          The invitation expires in 30 days. You can resend the invitation if needed.
        </p>
      </div>

      <div class="modal-actions" style="padding: 1.5rem;">
        <button class="btn-primary" onclick={closeInviteLinkModal}>
          Done
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .invitations-section {
    background: white;
    border-radius: var(--radius-md);
    padding: 1.5rem;
    box-shadow: var(--shadow-sm);
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1.5rem;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .section-header h2 {
    margin: 0;
    font-size: 1.25rem;
    color: var(--color-gray-900);
  }

  .section-description {
    margin: 0.25rem 0 0;
    color: var(--color-gray-600);
    font-size: 0.9rem;
  }

  /* Error banner uses shared .error-banner styles */

  /* Loading state uses shared .loading-state and .spinner styles */

  .access-denied, .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 2rem;
    text-align: center;
    color: var(--color-gray-600);
  }

  .access-denied svg, .empty-state svg {
    width: 48px;
    height: 48px;
    color: var(--color-gray-400);
    margin-bottom: 1rem;
  }

  .access-denied h3, .empty-state h3 {
    margin: 0 0 0.5rem;
    font-size: 1.1rem;
    color: var(--color-gray-900);
  }

  .access-denied p, .empty-state p {
    margin: 0;
    font-size: 0.9rem;
  }

  .empty-state .hint {
    margin-top: 0.5rem;
    font-size: 0.85rem;
    color: var(--color-gray-400);
  }

  /* Warehouses Info */
  .warehouses-info {
    background: var(--color-gray-50);
    border-radius: var(--radius-md);
    padding: 1rem;
    margin-bottom: 1.5rem;
  }

  .warehouses-info h4 {
    margin: 0 0 0.75rem;
    font-size: 0.9rem;
    color: var(--color-gray-900);
  }

  .warehouse-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .warehouse-tag {
    background: var(--color-info-bg);
    color: #1e40af;
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    font-size: 0.8rem;
    font-weight: 500;
  }

  /* Invitations List */
  .invitations-list {
    margin-bottom: 1.5rem;
  }

  .invitations-list h4 {
    margin: 0 0 1rem;
    font-size: 0.9rem;
    color: var(--color-gray-600);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .invitation-card {
    border: 1px solid var(--color-gray-200);
    border-radius: var(--radius-md);
    padding: 1rem;
    margin-bottom: 0.75rem;
    transition: box-shadow 0.2s;
  }

  .invitation-card:hover {
    box-shadow: var(--shadow-sm);
  }

  .invitation-card.accepted {
    border-color: var(--color-success-bg);
    background: #f0fdf4;
  }

  .invitation-card.inactive {
    opacity: 0.6;
    background: var(--color-gray-50);
  }

  .invitation-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
  }

  .company-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .company-avatar {
    width: 40px;
    height: 40px;
    border-radius: var(--radius-md);
    background: var(--gradient-primary);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 1rem;
  }

  .company-avatar.accepted {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  }

  .company-avatar.inactive {
    background: var(--color-gray-400);
  }

  .company-details {
    display: flex;
    flex-direction: column;
  }

  .company-name {
    font-weight: 600;
    color: var(--color-gray-900);
  }

  .contact-info {
    font-size: 0.85rem;
    color: var(--color-gray-600);
  }

  /* Status badges use shared .status-badge base styles */
  .status-badge {
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: capitalize;
  }

  .status-pending {
    background: var(--color-warning-bg);
    color: var(--color-warning);
  }

  .status-accepted {
    background: var(--color-success-bg);
    color: var(--color-success);
  }

  .status-expired {
    background: var(--color-error-bg);
    color: var(--color-error);
  }

  .status-revoked {
    background: var(--color-gray-200);
    color: var(--color-gray-700);
  }

  .invitation-meta {
    display: flex;
    gap: 1rem;
    font-size: 0.8rem;
    color: #666;
    margin-bottom: 0.75rem;
  }

  .invitation-actions {
    display: flex;
    gap: 0.5rem;
  }

  .btn-secondary-sm, .btn-danger-sm {
    padding: 0.375rem 0.75rem;
    border-radius: var(--radius-sm);
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    border: none;
    transition: all 0.2s;
  }

  .btn-secondary-sm {
    background: var(--color-gray-100);
    color: var(--color-gray-700);
  }

  .btn-secondary-sm:hover {
    background: var(--color-gray-200);
  }

  .btn-danger-sm {
    background: var(--color-error-bg);
    color: var(--color-error);
  }

  .btn-danger-sm:hover {
    background: #fecaca;
  }

  /* Expired Section */
  .expired-section {
    margin-top: 1.5rem;
  }

  .expired-section summary {
    cursor: pointer;
    color: var(--color-gray-600);
    font-size: 0.9rem;
    padding: 0.5rem 0;
  }

  .expired-section summary:hover {
    color: var(--color-gray-900);
  }

  .empty-invitations {
    text-align: center;
    padding: 2rem;
    color: var(--color-gray-600);
    font-size: 0.9rem;
  }

  /* Modal styles use shared .modal-overlay, .modal, .modal-header, .modal-body, .modal-actions */
  /* Form styles use shared .form-group, .form-error, .form-help */
  /* Button styles use shared .btn-primary, .btn-secondary, .btn-close */

  .modal form {
    padding: 1.5rem;
  }

  .checkbox-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
    background: var(--color-gray-50);
    border-radius: var(--radius-sm);
    border: 1px solid var(--color-gray-200);
  }

  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    cursor: pointer;
  }

  .checkbox-label input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }

  .info-box {
    display: flex;
    gap: 0.75rem;
    padding: 1rem;
    background: var(--color-info-bg);
    border-radius: var(--radius-md);
    margin: 1rem 0;
  }

  .info-box svg {
    width: 20px;
    height: 20px;
    color: var(--color-info);
    flex-shrink: 0;
  }

  .info-box strong {
    display: block;
    color: #1e40af;
    margin-bottom: 0.25rem;
    font-size: 0.9rem;
  }

  .info-box p {
    margin: 0;
    font-size: 0.85rem;
    color: #1e40af;
    line-height: 1.5;
  }

  /* Invite Link Modal Styles */
  .invite-link-container {
    display: flex;
    gap: 0.5rem;
    margin-top: 0.75rem;
  }

  .invite-link-input {
    flex: 1;
    padding: 0.75rem;
    border: 1px solid var(--color-gray-300);
    border-radius: var(--radius-sm);
    font-size: 0.85rem;
    font-family: monospace;
    background: var(--color-gray-50);
  }

  .btn-copy {
    padding: 0.75rem 1rem;
    background: var(--color-primary);
    color: white;
    border: none;
    border-radius: var(--radius-sm);
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;
    transition: background 0.2s;
  }

  .btn-copy:hover {
    background: var(--color-primary-hover);
  }

  .btn-copy svg {
    width: 16px;
    height: 16px;
  }

  /* Email Status Styles */
  .email-status {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border-radius: var(--radius-sm);
    font-size: 0.9rem;
  }

  .email-status.sending {
    background: #e0e7ff;
    color: #3730a3;
  }

  .email-status.success {
    background: var(--color-success-bg);
    color: var(--color-success);
  }

  .email-status.error {
    background: var(--color-error-bg);
    color: var(--color-error);
  }

  .email-status svg {
    flex-shrink: 0;
  }

  /* Small spinner uses shared .spinner-small */

  @media (max-width: 640px) {
    .invitation-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.75rem;
    }

    .invitation-meta {
      flex-direction: column;
      gap: 0.25rem;
    }

    .invitation-actions {
      width: 100%;
    }

    .btn-secondary-sm, .btn-danger-sm {
      flex: 1;
      justify-content: center;
    }
  }
</style>